import { useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUpdateProductPrice, useUpdateProductStock } from '../../hooks';
import { STOCK_STATUS_OPTIONS } from '../../model/options';
import type { Product, StockStatus } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

interface QuickDialogProps {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function toNumberOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isNaN(n) ? undefined : n;
}

/** Inline price edit (PUT /products/{id}/price) — fast repricing from the product list. */
export function QuickPriceDialog({ product, open, onClose, onResult }: QuickDialogProps) {
  const update = useUpdateProductPrice();
  const [price, setPrice] = useState('');
  const [oldPrice, setOldPrice] = useState('');

  useEffect(() => {
    if (open && product) {
      setPrice(product.price != null ? String(product.price) : '');
      setOldPrice(product.oldPrice != null ? String(product.oldPrice) : '');
    }
  }, [open, product]);

  const priceValue = toNumberOrUndefined(price);
  const valid = priceValue !== undefined && priceValue >= 0;

  const submit = () => {
    if (!product || priceValue === undefined) return;
    update.mutate(
      { productId: product.id, body: { price: priceValue, oldPrice: toNumberOrUndefined(oldPrice) } },
      {
        onSuccess: () => {
          onResult('success', `Цена обновлена: ${product.name}`);
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Изменить цену</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {product?.name}
          </Typography>
          <TextField
            label="Цена, ₽"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            error={price.trim() !== '' && !valid}
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
          />
          <TextField
            label="Старая цена, ₽ (для зачёркивания)"
            type="number"
            value={oldPrice}
            onChange={(e) => setOldPrice(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={update.isPending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={update.isPending || !valid}
          startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Inline stock + availability edit (PUT /products/{id}/stock). */
export function QuickStockDialog({ product, open, onClose, onResult }: QuickDialogProps) {
  const update = useUpdateProductStock();
  const [stockStatus, setStockStatus] = useState<StockStatus>('IN_STOCK');
  const [quantity, setQuantity] = useState('');

  useEffect(() => {
    if (open && product) {
      setStockStatus(product.stockStatus);
      setQuantity('');
    }
  }, [open, product]);

  const submit = () => {
    if (!product) return;
    update.mutate(
      { productId: product.id, body: { stockStatus, stockQuantity: toNumberOrUndefined(quantity) } },
      {
        onSuccess: () => {
          onResult('success', `Остаток обновлён: ${product.name}`);
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Наличие и остаток</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {product?.name}
          </Typography>
          <TextField
            select
            label="Наличие"
            value={stockStatus}
            onChange={(e) => setStockStatus(e.target.value as StockStatus)}
            fullWidth
          >
            {STOCK_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Количество на складе"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            helperText="Необязательно — оставьте пустым, чтобы не менять."
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={update.isPending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={update.isPending}
          startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
